import { Text } from '@oneaudi/unified-web-components';
import { useAtomValue } from 'jotai';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig, VATConfig } from '~/app/market-config';
import { useFootnotes } from '~/components/footnotes';
import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { Prices } from '~/components/prices';
import { PriceFragment, Status } from '~/graphql/__generated__/graphql';
import { useFinancing } from '~/graphql/hooks/use-financing';
import { state } from '~/storage';

export function ProductInformationText({
	status,
	price,
	monthlyRateOverride,
	pr7,
	vatConfig,
}: {
	status: Status;
	price: PriceFragment | undefined | null;
	monthlyRateOverride: number | undefined | null;
	pr7: string | undefined;
	vatConfig: VATConfig;
}) {
	const { getReference } = useFootnotes();
	const { config } = useMarketConfig();
	const { i18n } = useI18n();

	const { partRateByPr } = useFinancing();
	const monthlyRate = partRateByPr(pr7);
	const monthlyRateAvailable = monthlyRateOverride ?? monthlyRate;

	const { addFootnote, removeFootnote } = useFootnotes();
	const disclaimer = useAtomValue(state.financingDisclaimerText);

	if (!monthlyRateAvailable) {
		removeFootnote(MONTHLY_RATE_DIALOG_REMARK);
	} else if (
		config.enableFinancingServices &&
		!config.hideMonthlyRates &&
		config.showFinancingDisclaimerInDialogs &&
		!status.standard
	) {
		addFootnote(MONTHLY_RATE_DIALOG_REMARK, disclaimer);
	}

	return (
		<div className="flex flex-col">
			{!status.available && (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>{i18n('ngc.common.NotAvailable')}</Text>
			)}
			{status.standard ? (
				<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>{i18n('ngc.common.standard')}</Text>
			) : (
				<Prices
					totalPrice={price}
					pr7={pr7}
					monthlyRateOverride={monthlyRateOverride}
					monthlyRateDialog={true}
					monthlyRateDialogReference={getReference(MONTHLY_RATE_DIALOG_REMARK)}
					displayMode="rows"
					vatConfig={vatConfig}
				/>
			)}
		</div>
	);
}

/**
 * Extracts an item's quantity from its PR string.
 * If the PR string does not contain a quantity, `undefined` is returned.
 *
 * @example
 * getQuantity({ pr7: 'Z4KE071491 LT7*4' }) // '4'
 * getQuantity({ pr7: 'ABCDEFG' }) // undefined
 */
function getQuantity(item: { pr7: string }): string | undefined {
	// Matches `*{number}` at the end of string
	return item.pr7.match(/\*(\d+)$/)?.[1];
}

export function useItemQuantity(item: { pr7: string }) {
	const { i18n } = useI18n();
	const quantity = getQuantity(item);
	return quantity
		? {
				value: Number(quantity),
				formattedValue: `${quantity} ${i18n('ngc.common.piece')}`,
			}
		: null;
}
